import React, { useState, useEffect, useCallback } from "react";
import {
  Sparkles, CheckCircle2, Gauge, RefreshCw, AlertTriangle, Cpu, FlaskConical, Target,
} from "lucide-react";
import api from "@/lib/api";
import { Loading, SectionHeader, Card } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const ACTIONS = [
  { v: "", l: "Toutes les actions" },
  { v: "ask_lodge", l: "Ask Lodge" },
  { v: "analyze_market", l: "Analyze Market" },
  { v: "explain_opportunity", l: "Explain Opportunity" },
];
const ACTION_LABEL = { ask_lodge: "Ask Lodge", analyze_market: "Analyze Market", explain_opportunity: "Explain Opportunity" };
const CALIB_STATUS = {
  insufficient: { l: "Insuffisant", cls: "bg-slate-100 text-slate-600" },
  early: { l: "Précoce", cls: "bg-sky-100 text-sky-700" },
  initial_target_reached: { l: "Cible atteinte", cls: "bg-emerald-100 text-emerald-700" },
  stronger_sample: { l: "Échantillon renforcé", cls: "bg-emerald-100 text-emerald-700" },
};
const STATUSES = [
  { v: "", l: "Tous les statuts" },
  { v: "success", l: "Succès" },
  { v: "failed", l: "Échec" },
  { v: "rejected", l: "Rejeté" },
];
const DEMO = [
  { v: "", l: "Demo + Réel" },
  { v: "false", l: "Real Data uniquement" },
  { v: "true", l: "Demo uniquement" },
];
const PRESETS = [
  { v: "all", l: "Tout" },
  { v: "today", l: "Aujourd'hui" },
  { v: "7d", l: "7 jours" },
  { v: "30d", l: "30 jours" },
  { v: "month", l: "Mois en cours" },
];

const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR"));
const fmtPct = (n) => (n == null ? "—" : `${n}%`);
const fmtMs = (n) => (n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${n} ms`);

function presetRange(preset) {
  if (preset === "all") return { start: undefined, end: undefined };
  const now = new Date();
  const end = now.toISOString();
  let start;
  if (preset === "today") start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  else if (preset === "7d") start = new Date(now.getTime() - 7 * 864e5);
  else if (preset === "30d") start = new Date(now.getTime() - 30 * 864e5);
  else if (preset === "month") start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return { start: start.toISOString(), end };
}

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#3E7256]">
      <Icon size={16} />
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    </div>
    <p className="mt-2 font-heading text-3xl font-bold text-[#17201A]">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
  </div>
);

const QualityBar = ({ label, pct, tone, testid }) => (
  <div data-testid={testid}>
    <div className="mb-1 flex items-center justify-between text-xs">
      <span className="font-semibold text-slate-600">{label}</span>
      <span className="font-bold text-[#17201A]">{fmtPct(pct)}</span>
    </div>
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.min(100, pct || 0)}%` }} />
    </div>
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-[#3E7256] focus:outline-none">
    {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);

const Th = ({ children, right }) => (
  <th className={`whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, bold }) => (
  <td className={`whitespace-nowrap px-3 py-2 text-sm ${right ? "text-right" : "text-left"} ${bold ? "font-semibold text-[#17201A]" : "text-slate-600"}`}>{children}</td>
);

export default function AdminAiObservability() {
  const [orgs, setOrgs] = useState([]);
  const [preset, setPreset] = useState("all");
  const [orgId, setOrgId] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [demo, setDemo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [safety, setSafety] = useState(null);
  const [safetyErr, setSafetyErr] = useState(false);
  const [enableOpen, setEnableOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSafety = useCallback(() => {
    setSafetyErr(false);
    api.get("/admin/ai-safety").then((r) => setSafety(r.data)).catch(() => { setSafety(null); setSafetyErr(true); });
  }, []);
  useEffect(() => { loadSafety(); }, [loadSafety]);

  const disableProvider = () => {
    if (!window.confirm("Désactiver immédiatement tous les appels IA payants ?")) return;
    setBusy(true);
    api.patch("/admin/ai-safety", { provider_calls_enabled: false })
      .then((r) => setSafety(r.data)).catch(() => setSafetyErr(true)).finally(() => setBusy(false));
  };
  const enableProvider = () => {
    setBusy(true);
    api.patch("/admin/ai-safety", { provider_calls_enabled: true, confirmation: phrase })
      .then((r) => { setSafety(r.data); setEnableOpen(false); setPhrase(""); })
      .catch((e) => window.alert(e?.response?.data?.detail || "Échec de l'activation."))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    api.get("/admin/organizations").then((r) => setOrgs(r.data.organizations || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    const { start, end } = presetRange(preset);
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    if (orgId) params.organization_id = orgId;
    if (action) params.action_type = action;
    if (status) params.status = status;
    if (demo) params.is_demo = demo;
    api.get("/admin/ai-observability", { params })
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [preset, orgId, action, status, demo]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary;
  const uq = data?.usage_quality;
  const isEmpty = data && s && s.total_runs === 0;

  return (
    <div data-testid="admin-ai-observability">
      <SectionHeader title="AI Observability"
        subtitle="Usage technique de Lodge AI (shadow mode). Observabilité opérationnelle — aucune facturation." />

      {/* AI Financial Safety — provider execution kill switch (Phase 3c.10.2) */}
      {(() => {
        const st = safetyErr ? "LOCKED_FAIL_CLOSED" : safety?.effective_state;
        const enabled = st === "ENABLED";
        const tone = enabled ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
        const label = { ENABLED: "Activé", LOCKED_BY_ENV: "Verrouillé (environnement)", LOCKED_BY_RUNTIME: "Verrouillé (runtime)", LOCKED_FAIL_CLOSED: "Verrouillé (sécurité)" }[st] || "Chargement…";
        return (
          <Card className="mb-5 p-5" data-testid="ai-safety-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-1.5 font-heading text-sm font-bold text-[#17201A]">
                  <AlertTriangle size={15} className={enabled ? "text-amber-600" : "text-slate-400"} /> AI Financial Safety
                </h3>
                <p className="mt-0.5 text-xs text-slate-400">Contrôle d'infrastructure : autorise ou bloque toute exécution IA provider payante. Aucun crédit, aucune facturation.</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${tone}`} data-testid="ai-safety-state">AI Provider Execution — {label}</span>
            </div>

            {safety && (
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500" data-testid="ai-safety-meta">
                <span>Hard lock (env) : <b className={safety.hard_lock ? "text-slate-700" : "text-emerald-700"}>{safety.hard_lock ? "actif" : "inactif"}</b></span>
                <span>Runtime : <b>{safety.runtime_provider_calls_enabled ? "activé" : "désactivé"}</b></span>
                {safety.updated_by && <span>Dernier changement : {safety.updated_by}{safety.updated_at ? ` · ${new Date(safety.updated_at).toLocaleString("fr-FR")}` : ""}</span>}
              </div>
            )}

            <div className="mt-4">
              {enabled ? (
                <button onClick={disableProvider} disabled={busy} data-testid="ai-safety-disable-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#17201A] px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50">
                  Désactiver l'exécution provider
                </button>
              ) : (
                <div>
                  <button onClick={() => setEnableOpen(true)} disabled={busy || safety?.hard_lock || safetyErr} data-testid="ai-safety-enable-btn"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50">
                    Activer l'exécution provider
                  </button>
                  {safety?.hard_lock && (
                    <p className="mt-2 text-xs text-slate-500" data-testid="ai-safety-hardlock-note">
                      <b>Environment Hard Lock actif</b> — l'exécution provider ne peut pas être activée depuis l'Admin tant que le verrou environnement est en place.
                    </p>
                  )}
                </div>
              )}
            </div>

            {enableOpen && (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="ai-safety-enable-modal">
                <p className="text-sm font-semibold text-amber-900">Activer l'IA payante</p>
                <p className="mt-1 text-xs text-amber-800">Cette action peut générer des <b>frais provider réels</b>. Saisissez la phrase exacte <code className="rounded bg-white px-1">ENABLE PAID AI</code> pour confirmer.</p>
                <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="ENABLE PAID AI" data-testid="ai-safety-enable-phrase"
                  className="mt-3 w-full max-w-xs rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none" />
                <div className="mt-3 flex gap-2">
                  <button onClick={enableProvider} disabled={busy || phrase !== "ENABLE PAID AI"} data-testid="ai-safety-enable-confirm"
                    className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
                    Activer l'exécution payante
                  </button>
                  <button onClick={() => { setEnableOpen(false); setPhrase(""); }} data-testid="ai-safety-enable-cancel"
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
                </div>
              </div>
            )}
          </Card>
        );
      })()}

      {/* Filters */}
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="ai-obs-filters">
        <Select value={preset} onChange={setPreset} options={PRESETS} testid="ai-obs-filter-period" />
        <Select value={orgId} onChange={setOrgId}
          options={[{ v: "", l: "Toutes les organisations" }, ...orgs.map((o) => ({ v: o.id, l: o.name || o.id }))]}
          testid="ai-obs-filter-org" />
        <Select value={action} onChange={setAction} options={ACTIONS} testid="ai-obs-filter-action" />
        <Select value={status} onChange={setStatus} options={STATUSES} testid="ai-obs-filter-status" />
        <Select value={demo} onChange={setDemo} options={DEMO} testid="ai-obs-filter-demo" />
        <button onClick={load} data-testid="ai-obs-refresh"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {loading && <Loading />}

      {!loading && error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-12 text-center text-sm text-red-600" data-testid="ai-obs-error">
          <AlertTriangle size={18} className="mx-auto mb-2" />
          Impossible de charger les données d'observabilité. Veuillez réessayer.
        </div>
      )}

      {!loading && !error && isEmpty && (
        <div className="rounded-2xl border border-slate-200 bg-white py-12 text-center text-sm text-slate-400" data-testid="ai-obs-empty">
          Aucune utilisation IA pour cette période.
        </div>
      )}

      {!loading && !error && data && !isEmpty && (
        <div className="space-y-6">
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Kpi icon={Sparkles} label="AI Runs" value={fmtInt(s.total_runs)}
              sub={`${fmtInt(s.successful_runs)} succès · ${fmtInt(s.failed_runs)} échecs · ${fmtInt(s.rejected_runs)} rejetés`}
              testid="ai-obs-kpi-runs" />
            <Kpi icon={CheckCircle2} label="Success Rate" value={fmtPct(s.success_rate_pct)} testid="ai-obs-kpi-success" />
            <Kpi icon={Gauge} label="Actual Token Coverage" value={fmtPct(uq.actual_coverage_pct)}
              sub={`${fmtInt(uq.provider_called_runs)} runs appelant le provider`} testid="ai-obs-kpi-coverage" />
            <Kpi icon={RefreshCw} label="Retry Rate" value={fmtPct(s.retry_rate_pct)} testid="ai-obs-kpi-retry" />
          </div>

          {/* Usage quality + tokens */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="p-5" data-testid="ai-obs-quality">
              <h3 className="mb-1 font-heading text-sm font-bold text-[#17201A]">Qualité de mesure de l'usage</h3>
              <p className="mb-4 text-xs text-slate-400">Base : {uq.denominator}</p>
              <div className="space-y-3">
                <QualityBar label="Actual — usage provider entièrement mesuré" pct={uq.actual_coverage_pct} tone="bg-emerald-500" testid="ai-obs-q-actual" />
                <QualityBar label="Partial — mesure partielle (borne basse)" pct={uq.partial_coverage_pct} tone="bg-amber-500" testid="ai-obs-q-partial" />
                <QualityBar label="Estimated — usage provider indisponible" pct={uq.estimated_coverage_pct} tone="bg-slate-400" testid="ai-obs-q-estimated" />
              </div>
              <div className="mt-4 flex gap-4 text-xs text-slate-500">
                <span data-testid="ai-obs-q-actual-count"><b className="text-[#17201A]">{fmtInt(uq.actual_runs)}</b> actual</span>
                <span data-testid="ai-obs-q-partial-count"><b className="text-[#17201A]">{fmtInt(uq.partial_runs)}</b> partial</span>
                <span data-testid="ai-obs-q-estimated-count"><b className="text-[#17201A]">{fmtInt(uq.estimated_runs)}</b> estimated</span>
              </div>
            </Card>

            <Card className="p-5" data-testid="ai-obs-tokens">
              <h3 className="mb-1 font-heading text-sm font-bold text-[#17201A]">Tokens mesurés (Actual)</h3>
              <p className="mb-4 text-xs text-slate-400">Usage provider mesuré uniquement — les runs Partial/Estimated sont exclus.</p>
              <div className="grid grid-cols-3 gap-3">
                <div><p className="text-xs text-slate-400">Input</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtInt(data.tokens.actual_input_tokens)}</p></div>
                <div><p className="text-xs text-slate-400">Output</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtInt(data.tokens.actual_output_tokens)}</p></div>
                <div><p className="text-xs text-slate-400">Total</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtInt(data.tokens.actual_total_tokens)}</p></div>
              </div>
              <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-400" data-testid="ai-obs-cost">
                Coût provider — <span className="font-semibold text-slate-500">{data.provider_cost_status === "configured" ? "Configuré (voir Cost Intelligence)" : "Non configuré"}</span>
              </p>
            </Card>
          </div>

          {/* Latency + status/errors + demo/real */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="p-5" data-testid="ai-obs-latency">
              <h3 className="mb-3 font-heading text-sm font-bold text-[#17201A]">Latence (global)</h3>
              <div className="flex items-center justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">p50</span><span className="font-semibold text-[#17201A]">{fmtMs(data.latency.p50_latency_ms)}</span></div>
              <div className="flex items-center justify-between py-2 text-sm"><span className="text-slate-500">p95</span><span className="font-semibold text-[#17201A]">{fmtMs(data.latency.p95_latency_ms)}</span></div>
            </Card>

            <Card className="p-5" data-testid="ai-obs-status">
              <h3 className="mb-3 font-heading text-sm font-bold text-[#17201A]">Statuts & erreurs</h3>
              <div className="flex gap-2">
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid="ai-obs-status-success">Succès {fmtInt(data.by_status.success)}</span>
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid="ai-obs-status-failed">Échec {fmtInt(data.by_status.failed)}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600" data-testid="ai-obs-status-rejected">Rejeté {fmtInt(data.by_status.rejected)}</span>
              </div>
              {data.errors.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="ai-obs-errors">
                  {data.errors.map((e) => (
                    <div key={e.error_type} className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{e.error_type}</span><span className="font-semibold text-[#17201A]">{fmtInt(e.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5" data-testid="ai-obs-demo-real">
              <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-bold text-[#17201A]"><FlaskConical size={14} /> Demo vs Real Data</h3>
              <div className="flex items-center justify-between border-b border-slate-100 py-2 text-sm"><span className="text-slate-500">Real Data</span><span className="font-semibold text-[#17201A]" data-testid="ai-obs-real-count">{fmtInt(data.demo_vs_real.real_runs)}</span></div>
              <div className="flex items-center justify-between py-2 text-sm"><span className="text-slate-500">Demo</span><span className="font-semibold text-[#17201A]" data-testid="ai-obs-demo-count">{fmtInt(data.demo_vs_real.demo_runs)}</span></div>
            </Card>
          </div>

          {/* Models */}
          <Card className="p-5" data-testid="ai-obs-models">
            <h3 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-bold text-[#17201A]"><Cpu size={14} /> Modèles</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Configuré</p>
                {Object.entries(data.models.configured_distribution).map(([m, c]) => (
                  <div key={m} className="flex items-center justify-between py-1 text-sm"><span className="text-slate-500">{m}</span><span className="font-semibold text-[#17201A]">{fmtInt(c)}</span></div>
                ))}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Exécuté (runtime)</p>
                {Object.entries(data.models.executed_distribution).map(([m, c]) => (
                  <div key={m} className="flex items-center justify-between py-1 text-sm"><span className="text-slate-500">{m}</span><span className="font-semibold text-[#17201A]">{fmtInt(c)}</span></div>
                ))}
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Divergence</p>
                <p className="font-heading text-2xl font-bold text-[#17201A]" data-testid="ai-obs-divergence-rate">{fmtPct(data.models.model_divergence_rate_pct)}</p>
                {data.models.divergence_breakdown.map((b) => (
                  <p key={b.transition} className="mt-1 text-xs text-amber-600">{b.transition} · {fmtInt(b.count)}</p>
                ))}
              </div>
            </div>
          </Card>

          {/* Cost Intelligence */}
          {data.provider_cost_status === "configured" && data.cost && (
            <Card className="p-5" data-testid="ai-obs-cost-intel">
              <h3 className="mb-1 flex items-center gap-1.5 font-heading text-sm font-bold text-[#17201A]">Cost Intelligence (provider, interne)</h3>
              <p className="mb-4 text-xs text-slate-400">Mesuré = usage actual + tarif matched. Fallback / Partial (borne basse) / Estimated séparés. Devises jamais additionnées.</p>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div data-testid="ai-obs-cost-coverage-match"><p className="text-xs text-slate-400">Pricing Match Coverage</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtPct(data.cost.pricing_match_coverage_pct)}</p></div>
                <div data-testid="ai-obs-cost-coverage-high"><p className="text-xs text-slate-400">High-Confidence Coverage</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtPct(data.cost.high_confidence_cost_coverage_pct)}</p></div>
                <div data-testid="ai-obs-cost-priced"><p className="text-xs text-slate-400">Priced Runs</p><p className="font-heading text-2xl font-bold text-[#17201A]">{fmtInt(data.cost.priced_runs)}</p></div>
                <div data-testid="ai-obs-cost-unpriced"><p className="text-xs text-slate-400">Unpriced Runs</p><p className={`font-heading text-2xl font-bold ${data.cost.unpriced_runs > 0 ? "text-amber-600" : "text-[#17201A]"}`}>{fmtInt(data.cost.unpriced_runs)}</p></div>
              </div>
              {Object.entries(data.cost.by_currency || {}).map(([cur, b]) => (
                <div key={cur} className="mt-4 rounded-xl border border-slate-100 p-3" data-testid={`ai-obs-cost-cur-${cur}`}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{cur}</p>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">
                    <div><span className="text-slate-400">Measured</span><p className="font-semibold text-emerald-700">{b.measured} {cur}</p></div>
                    <div><span className="text-slate-400">Fallback</span><p className="font-semibold text-slate-600">{b.fallback} {cur}</p></div>
                    <div><span className="text-slate-400">Partial (≥ borne basse)</span><p className="font-semibold text-amber-600">{b.partial_lower_bound} {cur}</p></div>
                    <div><span className="text-slate-400">Estimated</span><p className="font-semibold text-slate-500">{b.estimated} {cur}</p></div>
                  </div>
                </div>
              ))}
              {data.cost.pricing_error_runs > 0 && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-red-600" data-testid="ai-obs-cost-errors"><AlertTriangle size={13} /> {data.cost.pricing_error_runs} run(s) avec résolution tarifaire ambiguë.</p>
              )}
              {data.pricing_gaps && data.pricing_gaps.length > 0 && (
                <div className="mt-4" data-testid="ai-obs-pricing-gaps">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-600">Usage IA non tarifé</p>
                  {data.pricing_gaps.map((g, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1 text-xs"><span className="text-slate-500">{g.provider} / {g.model}</span><span className="font-semibold text-[#17201A]">{fmtInt(g.count)}</span></div>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-400">Demo mesuré vs Real Data mesuré : voir répartition par organisation/action. Aucune conversion FX, aucun crédit/marge.</p>
            </Card>
          )}

          {/* AI Calibration Maturity (Phase 3B.5.b.3c.8) */}
          {data.calibration_maturity && (
            <Card className="p-5" data-testid="ai-obs-calibration">
              <h3 className="mb-1 flex items-center gap-1.5 font-heading text-sm font-bold text-[#17201A]">
                <Target size={15} className="text-[#17201A]" /> AI Calibration Maturity
              </h3>
              <p className="mb-4 text-xs text-slate-400">
                Suit le volume d'usage IA mesuré et correctement tarifé disponible pour une future calibration des Lodge Credits.
                Atteindre la cible ne modifie <b>ni les prix, ni les abonnements, ni la facturation client</b>.
              </p>

              {/* Global summary */}
              <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                <div data-testid="ai-obs-calib-at-target">
                  <p className="text-xs text-slate-400">Actions à la cible</p>
                  <p className="font-heading text-2xl font-bold text-[#17201A]">{data.calibration_maturity.actions_at_target} / {data.calibration_maturity.total_actions}</p>
                </div>
                <div data-testid="ai-obs-calib-coverage">
                  <p className="text-xs text-slate-400">High-Confidence Coverage</p>
                  <p className="font-heading text-2xl font-bold text-[#17201A]">{fmtPct(data.calibration_maturity.coverage_pct)}</p>
                </div>
                <div data-testid="ai-obs-calib-orgs">
                  <p className="text-xs text-slate-400">Organisations</p>
                  <p className="font-heading text-2xl font-bold text-[#17201A]">{fmtInt(data.calibration_maturity.qualifying_organizations)}</p>
                </div>
                <div data-testid="ai-obs-calib-checkpoints">
                  <p className="text-xs text-slate-400">Checkpoints</p>
                  <div className="mt-1 flex flex-col gap-1">
                    <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${data.calibration_maturity.coverage_checkpoint === "passed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="ai-obs-calib-coverage-checkpoint">
                      Coverage Recovery — {data.calibration_maturity.coverage_checkpoint === "passed" ? "Passé" : "En cours"}
                    </span>
                    <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${data.calibration_maturity.calibration_checkpoint === "initial_target_reached" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`} data-testid="ai-obs-calib-sample-checkpoint">
                      Credit Calibration Sample — {data.calibration_maturity.calibration_checkpoint === "initial_target_reached" ? "Cible atteinte" : "En cours"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Per-action progress */}
              <div className="space-y-3">
                {ACTIONS.filter((a) => a.v && data.calibration_maturity.actions[a.v]).map((a) => {
                  const m = data.calibration_maturity.actions[a.v];
                  const st = CALIB_STATUS[m.status] || CALIB_STATUS.insufficient;
                  const reached = m.qualifying_runs >= m.target;
                  return (
                    <div key={a.v} className="rounded-xl border border-slate-100 p-3" data-testid={`ai-obs-calib-action-${a.v}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#17201A]">{ACTION_LABEL[a.v]}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-500" data-testid={`ai-obs-calib-count-${a.v}`}>
                            {reached ? `${fmtInt(m.qualifying_runs)} · cible atteinte` : `${m.qualifying_runs} / ${m.target}`}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st.cls}`} data-testid={`ai-obs-calib-status-${a.v}`}>{st.l}</span>
                        </div>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#17201A] transition-all" style={{ width: `${m.progress_pct}%` }} data-testid={`ai-obs-calib-bar-${a.v}`} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                        <span>Médiane {m.median_measured_cost ? `${m.median_measured_cost} USD` : "—"}</span>
                        <span>P90 {m.p90_sample_sufficient ? (m.p90_measured_cost ? `${m.p90_measured_cost} USD` : "—") : "échantillon encore insuffisant"}</span>
                        <span>{fmtInt(m.organizations_represented)} org.</span>
                        {m.failed_qualifying_provider_runs > 0 && <span>{m.failed_qualifying_provider_runs} échec(s) provider (hors cible)</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {data.calibration_maturity.actions_at_target === data.calibration_maturity.total_actions ? (
                <p className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="ai-obs-calib-target-note">
                  Cible de calibration initiale atteinte — revue économique recommandée. (Aucun crédit n'est créé automatiquement.)
                </p>
              ) : (
                <p className="mt-4 text-xs text-slate-400" data-testid="ai-obs-calib-scope">
                  {data.calibration_maturity.scope_note} Cible = {data.calibration_maturity.target_per_action} runs qualifiants / action (suivi interne uniquement).
                </p>
              )}
            </Card>
          )}

          {/* By action */}
          <Card data-testid="ai-obs-by-action">
            <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-heading text-sm font-bold text-[#17201A]">Usage par action</h3></div>
            <SelectableTable rows={data.by_action} rowId={(a) => a.action_type} filename="usage-par-action.csv"
              ariaLabel="Usage par action — sélection et défilement"
              csvColumns={[
                { label: "Action", get: (a) => ACTION_LABEL[a.action_type] || a.action_type },
                { label: "Runs", get: (a) => a.runs },
                { label: "Succès", get: (a) => a.successful },
                { label: "Échec", get: (a) => a.failed },
                { label: "Rejet", get: (a) => a.rejected },
                { label: "Retry %", get: (a) => a.retry_rate_pct },
                { label: "Actual Cov. %", get: (a) => a.actual_coverage_pct },
                { label: "Tokens (actual)", get: (a) => a.actual_total_tokens },
              ]}>
              <table className="w-full">
                <thead className="bg-slate-50"><tr>
                  <Th><SelectAllHeaderCheckbox /></Th>
                  <Th>Action</Th><Th right>Runs</Th><Th right>Succès</Th><Th right>Échec/Rejet</Th>
                  <Th right>Retry</Th><Th right>Actual Cov.</Th><Th right>Tokens (actual)</Th>
                  <Th right>Médiane*</Th><Th right>P90*</Th><Th right>Lat. p50</Th><Th right>Lat. p95</Th><Th right>Demo/Réel</Th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.by_action.map((a) => (
                    <tr key={a.action_type} data-testid={`ai-obs-action-${a.action_type}`}>
                      <Td><RowCheckbox id={a.action_type} label={`Sélectionner ${a.action_type}`} /></Td>
                      <Td bold>{ACTION_LABEL[a.action_type] || a.action_type}</Td>
                      <Td right bold>{fmtInt(a.runs)}</Td>
                      <Td right>{fmtInt(a.successful)}</Td>
                      <Td right>{fmtInt(a.failed)}/{fmtInt(a.rejected)}</Td>
                      <Td right>{fmtPct(a.retry_rate_pct)}</Td>
                      <Td right>{fmtPct(a.actual_coverage_pct)}</Td>
                      <Td right>{fmtInt(a.actual_total_tokens)}</Td>
                      <Td right>{fmtInt(a.median_actual_tokens)}</Td>
                      <Td right>{fmtInt(a.p90_actual_tokens)}</Td>
                      <Td right>{fmtMs(a.p50_latency_ms)}</Td>
                      <Td right>{fmtMs(a.p95_latency_ms)}</Td>
                      <Td right>{fmtInt(a.demo_runs)}/{fmtInt(a.real_runs)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SelectableTable>
            <p className="px-5 py-2 text-xs text-slate-400">* Médiane / P90 calculées sur les tokens des runs <b>Actual</b> uniquement.</p>
          </Card>

          {/* By organization */}
          <Card data-testid="ai-obs-by-org">
            <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-heading text-sm font-bold text-[#17201A]">Usage IA par organisation</h3></div>
            <SelectableTable rows={data.by_organization} rowId={(o) => o.organization_id} filename="usage-par-organisation.csv"
              ariaLabel="Usage par organisation — sélection et défilement"
              csvColumns={[
                { label: "Organisation", get: (o) => o.organization_name || o.organization_id },
                { label: "Runs", get: (o) => o.runs },
                { label: "Succès", get: (o) => o.successful },
                { label: "Échec", get: (o) => o.failed },
                { label: "Rejet", get: (o) => o.rejected },
                { label: "Retry %", get: (o) => o.retry_rate_pct },
                { label: "Tokens (actual)", get: (o) => o.actual_total_tokens },
                { label: "Actual Cov. %", get: (o) => o.actual_coverage_pct },
              ]}>
              <table className="w-full">
                <thead className="bg-slate-50"><tr>
                  <Th><SelectAllHeaderCheckbox /></Th>
                  <Th>Organisation</Th><Th right>Runs</Th><Th right>Succès</Th><Th right>Échec/Rejet</Th>
                  <Th right>Retry</Th><Th right>Tokens (actual)</Th><Th right>Actual Cov.</Th><Th right>Demo/Réel</Th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.by_organization.map((o) => (
                    <tr key={o.organization_id} data-testid="ai-obs-org-row">
                      <Td><RowCheckbox id={o.organization_id} label={`Sélectionner ${o.organization_name || o.organization_id}`} /></Td>
                      <Td bold>{o.organization_name || o.organization_id}</Td>
                      <Td right bold>{fmtInt(o.runs)}</Td>
                      <Td right>{fmtInt(o.successful)}</Td>
                      <Td right>{fmtInt(o.failed)}/{fmtInt(o.rejected)}</Td>
                      <Td right>{fmtPct(o.retry_rate_pct)}</Td>
                      <Td right>{fmtInt(o.actual_total_tokens)}</Td>
                      <Td right>{fmtPct(o.actual_coverage_pct)}</Td>
                      <Td right>{fmtInt(o.demo_runs)}/{fmtInt(o.real_runs)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SelectableTable>
          </Card>
        </div>
      )}
    </div>
  );
}
